import { useContext, useEffect, useMemo, useState } from 'react';
import { CostTypeRef, ECreditType } from '@leedpulse-monorepo/enums';
import CompanyContext from '../../contexts/company/CompanyContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import PlansContext from '../../contexts/plans/PlansContext';
import { getCompanySubscriptions } from '../../../domain/use_cases/subscription/getCompanySubscriptions';
import { formatDate, isUnlimitedLeedbucks, leedbucksRateOf } from '../../helpers';
import { ISubscription, IPlan } from '@leedpulse-monorepo/interfaces';
import { nextCreditsResetAt, runningCreditsPromo } from '@leedpulse-monorepo/utils';
import { LeedbucksConfig } from '../../../domain/use_cases/leedflow/getLeedbucksConfig';

interface SubscriptionWithPlan {
    subscription: ISubscription;
    plan: IPlan;
}

export interface LeedbucksUsage {
    used: number;
    total: number;
    remaining: number;
    usagePercentage: number; // Consumed percentage — raw, may exceed 100 in case of overage. Clamp before setting a gauge width.
    nextResetDate: string;
    nextResetLabel: string;
    isUnlimited: boolean;
    lowCreditCount: number; // Subscriptions with less than 20% leedbucks left
    overageCount: number; // Subscriptions with negative leedbucks with PAYG enabled
    overageAmount: number; // Total extra cost
    exhaustedCount: number; // Subscriptions with 0 leedbucks with PAYG disabled
    minutesEquivalent: number | null; // What the remaining credits buy in calls, each number at its own rate; null without rates, Infinity when every number calls for free
    smsEquivalent: number | null; // Same in SMS
}

const DEFAULT_LEEDBUCKS_USAGE: LeedbucksUsage = {
    used: 0,
    total: 0,
    remaining: 0,
    usagePercentage: 0,
    nextResetDate: '',
    nextResetLabel: '',
    isUnlimited: false,
    lowCreditCount: 0,
    overageCount: 0,
    overageAmount: 0,
    exhaustedCount: 0,
    minutesEquivalent: null,
    smsEquivalent: null,
};

/**
 * Aggregates the unified leedbucks credit across every Leedflow subscription of
 * the active company.
 *
 * – Total     → sum of each subscription's running promo, its **plan** `credits.leedbucks` without one
 * – Used      → sum of `planTotal - current` (overuse pushes it above total)
 * – Remaining → sum of `max(current, 0)`
 * – Unlimited → subscriptions whose plan leedbucks ≥ UNLIMITED_THRESHOLD are
 *   excluded from the arithmetic; if all are unlimited the result is flagged.
 */
export const useCompanyCredits = (leedbucksConfig: LeedbucksConfig) => {
    const { activeCompany } = useContext(CompanyContext);
    const { authentication } = useContext(AuthenticationContext);
    const { fetchPlanById } = useContext(PlansContext);

    const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
    const [planMap, setPlanMap] = useState<Map<string, IPlan>>(new Map());
    const [isLoading, setIsLoading] = useState(true);

    // ── 1. Fetch subscriptions when active company changes ──────────────────
    useEffect(() => {
        if (!activeCompany || !authentication) {
            // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
            // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
            // external system") ; le setState synchrone est le reset qui precede l'appel.
            // eslint-disable-next-line react/set-state-in-effect
            setSubscriptions([]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        getCompanySubscriptions({ company_id: activeCompany.id }, authentication)
            .then((fetched) => {
                if (!cancelled) setSubscriptions(fetched);
            })
            .catch((err) => {
                console.error('useCompanyCredits – error fetching subscriptions:', err);
                if (!cancelled) setSubscriptions([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [activeCompany, authentication]);

    // ── 2. Resolve plans for every subscription (uses PlansContext cache) ───
    useEffect(() => {
        const uniquePlanIds = [
            ...new Set(subscriptions.map((s) => s.id_plan).filter(Boolean)),
        ];

        if (uniquePlanIds.length === 0) {
            // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
            // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
            // external system") ; le setState synchrone est le reset qui precede l'appel.
            // eslint-disable-next-line react/set-state-in-effect
            setPlanMap(new Map());
            return;
        }

        let cancelled = false;

        const resolve = async () => {
            const entries: [string, IPlan][] = [];
            for (const planId of uniquePlanIds) {
                const plan = await fetchPlanById(planId);
                if (plan && !cancelled) entries.push([planId, plan]);
            }
            if (!cancelled) {
                setPlanMap(new Map(entries));
            }
        };
        resolve();

        return () => {
            cancelled = true;
        };
    }, [subscriptions, fetchPlanById]);

    // ── 3. Calculate aggregated leedbucks usage ─────────────────────────────
    const creditsUsage: LeedbucksUsage = useMemo(() => {
        const pairs: SubscriptionWithPlan[] = subscriptions
            .map((sub) => {
                const plan = planMap.get(sub.id_plan);
                return plan ? { subscription: sub, plan } : null;
            })
            .filter((p): p is SubscriptionWithPlan => p !== null);

        if (pairs.length === 0) return DEFAULT_LEEDBUCKS_USAGE;

        // The backend resolves the credits cycle and stores its end on the subscription: the
        // jetons are monthly whatever the billing interval, and an annual billing date is a
        // year ahead. It used to be derived from next_billing_date by walking the months back.
        const iNextReset = pairs
            .map(({ subscription }) => subscription.credits_period_end)
            // The reset never precedes the end: checking the end first skips the lookup
            // eslint-disable-next-line react/purity
            .filter((end): end is number => !!end && (end > Date.now() || nextCreditsResetAt(end) > Date.now())) // A paused number keeps a frozen, past end
            .sort((a, b) => a - b)[0];

        const nextResetDate = iNextReset
            ? formatDate(iNextReset)
            : '';
        const nextResetLabel = iNextReset ? formatDate(nextCreditsResetAt(iNextReset), { time: true, until: true }) : '';

        // Exclude "unlimited" plans from the arithmetic (they may use huge/negative
        // tracking values that must never impact company-level usage).
        const finitePairs = pairs.filter(
            ({ plan }) => !isUnlimitedLeedbucks(plan.credits?.[ECreditType.leedbucks] || 0)
        );

        if (finitePairs.length === 0) {
            return {
                used: 0,
                total: Infinity,
                remaining: Infinity,
                usagePercentage: 0,
                nextResetDate,
                nextResetLabel,
                isUnlimited: true,
                lowCreditCount: 0,
                overageCount: 0,
                overageAmount: 0,
                exhaustedCount: 0,
                minutesEquivalent: null,
                smsEquivalent: null,
            };
        }

        let total = 0;
        let used = 0;
        let remaining = 0;
        let lowCreditCount = 0;
        let overageCount = 0;
        let overageAmount = 0;
        let exhaustedCount = 0;
        // Each number counted at its own rates: a promo or override changes what its credits buy
        let minutesEquivalent: number | null = null;
        let smsEquivalent: number | null = null;
        // A number getting the action for free is left out, as unlimited plans are: unlimited only when all of them do
        let allCallsFree = true;
        let allSmsFree = true;

        // eslint-disable-next-line react/purity
        const iNow = Date.now();
        for (const { plan, subscription } of finitePairs) {
            // Same rule as the grant: the running promo replaces the plan's allocation
            const planTotal = runningCreditsPromo(subscription.leedbucks_credits_temp, iNow)?.leedbucks ?? (plan.credits?.[ECreditType.leedbucks] || 0);
            const current = subscription.credits?.[ECreditType.leedbucks] || 0;
            const paygEnabled = subscription.settings?.payg_enabled !== false;

            total += planTotal;
            used += planTotal - current;
            remaining += Math.max(0, current);

            const callRate = leedbucksRateOf(leedbucksConfig, CostTypeRef.CALL, subscription.id);
            const smsRate = leedbucksRateOf(leedbucksConfig, CostTypeRef.SMS_OUTBOUND, subscription.id);
            if (callRate) minutesEquivalent = (minutesEquivalent ?? 0) + Math.max(0, current) / callRate;
            if (smsRate) smsEquivalent = (smsEquivalent ?? 0) + Math.max(0, current) / smsRate;
            if (callRate !== 0) allCallsFree = false;
            if (smsRate !== 0) allSmsFree = false;

            if (current < 0 && paygEnabled) {
                overageCount++;
                overageAmount += -current * (plan.payg?.[ECreditType.leedbucks] || 0);
            } else if (current <= 0) {
                exhaustedCount++;
            } else if (planTotal > 0 && current / planTotal < 0.2) {
                lowCreditCount++;
            }
        }

        used = Math.max(0, used);
        const usagePercentage = total > 0 ? Math.round((used / total) * 100) : 0;

        return {
            used: Math.round(used),
            total,
            remaining: Math.round(remaining),
            usagePercentage,
            nextResetDate,
            nextResetLabel,
            isUnlimited: false,
            lowCreditCount,
            overageCount,
            overageAmount,
            exhaustedCount,
            minutesEquivalent: allCallsFree ? Infinity : minutesEquivalent === null ? null : Math.floor(minutesEquivalent),
            smsEquivalent: allSmsFree ? Infinity : smsEquivalent === null ? null : Math.floor(smsEquivalent),
        };
    }, [subscriptions, planMap, leedbucksConfig]);

    const companyPlans = useMemo(() => [...planMap.values()], [planMap]);

    const creditsLoading = isLoading || (subscriptions.length > 0 && planMap.size === 0);

    return {
        creditsUsage,
        companyPlans,
        isLoading: creditsLoading,
    };
};
